import _async_to_generator from "@swc/helpers/src/_async_to_generator.mjs";
import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_without_properties from "@swc/helpers/src/_object_without_properties.mjs";
import _to_consumable_array from "@swc/helpers/src/_to_consumable_array.mjs";
import _ts_generator from "@swc/helpers/src/_ts_generator.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useCallback, useState } from "react";
import { useTranslations } from "next-intl";
import Search from "@engineering/icons/search";
import { UiLegacy } from "@silvertours/front-legacy-shared";
import { Ui, Button, Theme } from "@silvertours/front-shared";
import { analytics, useSiteInfo } from "@silvertours/front-entities";
import { TooltipBadge } from "@silvertours/front-legacy-entities";
import { SearchFormOptions } from "./SearchFormOptions";
import { SearchInput } from "./SearchInput";
import { AgeDropdown } from "./AgeDropdown";
import { Logo } from "./Logo";
import { SearchProvider, useSearchContext } from "./context";
import { useSubmitHandler, useSession } from "./hooks";
import { StyledForm, Header, Title, Subtitle, ButtonWrapper, FooterLink, FormContainer, CloseIcon, OptionsOpenIcon, OptionsCloseIcon } from "./SearchForm.styles";
import { matchError } from "./hooks/useSubmitHandler";
import { isFormError } from "./context/types";
import { getOptionsParam } from "./utils";
var SearchForm = function(param) {
    var customColor = param.customColor, isAffiliate = param.isAffiliate, stageTitle = param.stageTitle;
    var ref = useSiteInfo(), featureToggles = ref.settings.featureToggles;
    var ref1 = useSearchContext(), setCustomerAge = ref1.setCustomerAge, setDate = ref1.setDate, setError = ref1.setError, setShowDestLocation = ref1.setShowDestLocation, state = ref1.state;
    var t = useTranslations("features.stageLegacy.stage.searchForm");
    var submitHandler = useSubmitHandler();
    var ref2 = useSession(), trackClickTimestamp = ref2.trackClickTimestamp, removeClickTimestamp = ref2.removeClickTimestamp;
    var ref3 = useState(false), loading = ref3[0], setLoading = ref3[1];
    var isMobileTouchDevice = Theme.useIsMobileTouchDevice();
    var ref4 = useState(false), showOptions = ref4[0], setShowOptions = ref4[1];
    var ref5 = useState([]), checkedOptions = ref5[0], setCheckedOptions = ref5[1];
    // Show or hide the options on the right side of the form
    var toggleOptions = function() {
        var showOptionsValue = !showOptions;
        setShowOptions(showOptionsValue);
        analytics.gtm.trackToggleMoreOptions(showOptionsValue);
    };
    var handleOptionsChange = function(optionsId) {
        var checked = checkedOptions;
        if (checked.includes(optionsId)) {
            checked = checked.filter(function(key) {
                return key !== optionsId;
            });
        } else {
            checked = _to_consumable_array(checked).concat([
                optionsId
            ]);
        }
        setCheckedOptions(checked);
    };
    var onError = useCallback(function(error) {
        setLoading(false);
        removeClickTimestamp();
        var ref = matchError(error), kind = ref.type, message = ref.message;
        if (isFormError(kind)) {
            setError({
                kind: kind,
                message: message
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var handleSearchSubmit = function() {
        var _ref = _async_to_generator(function() {
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        setLoading(true);
                        trackClickTimestamp();
                        return [
                            4,
                            submitHandler(state, getOptionsParam(checkedOptions), function() {
                                setLoading(false);
                            }, onError)
                        ];
                    case 1:
                        _state.sent();
                        return [
                            2
                        ];
                }
            });
        });
        return function handleSearchSubmit() {
            return _ref.apply(this, arguments);
        };
    }();
    var handleSubmit = function() {
        var _ref = _async_to_generator(function(event) {
            return _ts_generator(this, function(_state) {
                event.preventDefault();
                if (loading) {
                    return [
                        2
                    ];
                }
                handleSearchSubmit();
                return [
                    2
                ];
            });
        });
        return function handleSubmit(event) {
            return _ref.apply(this, arguments);
        };
    }();
    if (!state) {
        return null;
    }
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs(Header, {
                children: [
                    /*#__PURE__*/ _jsx(Title, {
                        customColor: customColor,
                        children: stageTitle || t("title")
                    }),
                    !isAffiliate && /*#__PURE__*/ _jsx(Subtitle, {
                        children: t("subtitle")
                    }),
                    featureToggles.useSavingBadge ? /*#__PURE__*/ _jsx(TooltipBadge, {
                        badgeText: t("savingBadge"),
                        tooltipTitle: t("savingBadge"),
                        tooltipText: t.raw("saving.tooltip.text"),
                        onHover: function() {
                            return analytics.gtm.trackSavingHover();
                        }
                    }) : /*#__PURE__*/ _jsx(TooltipBadge, {
                        badgeText: t("cancellationBadge"),
                        tooltipTitle: t("cancellationBadge"),
                        tooltipText: t.raw("cancellation.tooltip.text"),
                        onHover: function() {
                            return analytics.gtm.trackCancellationHover();
                        }
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(StyledForm, {
                onSubmit: handleSubmit,
                id: "search",
                role: "search",
                children: [
                    /*#__PURE__*/ _jsxs(FormContainer, {
                        children: [
                            /*#__PURE__*/ _jsx(SearchInput, {
                                id: "dep",
                                onServiceNumbersSubmit: handleSearchSubmit,
                                history: state.history,
                                direction: "departure",
                                label: t("departure.label"),
                                placeholder: t("departure.placeholder"),
                                icon: /*#__PURE__*/ _jsx(Search, {})
                            }),
                            !state.showDestLocation && /*#__PURE__*/ _jsx(UiLegacy.Checkbox, {
                                label: t("destination.label"),
                                id: "destSelect",
                                checked: state.showDestLocation,
                                onChange: function() {
                                    return setShowDestLocation(true);
                                }
                            }),
                            state.showDestLocation && /*#__PURE__*/ _jsx(SearchInput, {
                                id: "dest",
                                direction: "destination",
                                label: t("destination.label"),
                                placeholder: t("destination.placeholder"),
                                icon: /*#__PURE__*/ _jsx(CloseIcon, {
                                    className: "inputCloseIcon",
                                    onClick: function() {
                                        return setShowDestLocation(false);
                                    }
                                })
                            }),
                            /*#__PURE__*/ _jsx(UiLegacy.DateRangePicker, {
                                from: new Date(state.depDate),
                                to: new Date(state.destDate),
                                error: state.dateError || "",
                                onDatesChange: function(dates) {
                                    setDate("departure", dates.from);
                                    setDate("destination", dates.to);
                                },
                                vertical: isMobileTouchDevice
                            }),
                            /*#__PURE__*/ _jsx(AgeDropdown, {
                                value: state.customerAge,
                                label: t("age.label"),
                                onChange: setCustomerAge
                            }),
                            /*#__PURE__*/ _jsx(ButtonWrapper, {
                                customColor: customColor,
                                children: /*#__PURE__*/ _jsx(Button, {
                                    id: "find",
                                    loading: loading,
                                    type: "submit",
                                    fullWidth: true,
                                    textId: "find-button-text",
                                    children: t("button.label")
                                })
                            }),
                            state.axiosError && /*#__PURE__*/ _jsx(Ui.ErrorMessage, {
                                children: t("errorTexts.request")
                            }),
                            isAffiliate && !isMobileTouchDevice && /*#__PURE__*/ _jsx(Logo, {}),
                            !isAffiliate && /*#__PURE__*/ _jsx(FooterLink, {
                                iconOnLeft: showOptions,
                                children: /*#__PURE__*/ _jsxs(Ui.TextLink, {
                                    id: "more-options-text",
                                    as: "button",
                                    onClick: toggleOptions,
                                    children: [
                                        t("options.label"),
                                        " ",
                                        checkedOptions.length > 0 && "(".concat(checkedOptions.length, ")"),
                                        showOptions ? /*#__PURE__*/ _jsx(OptionsOpenIcon, {
                                            id: "more-options-open-icon"
                                        }) : /*#__PURE__*/ _jsx(OptionsCloseIcon, {
                                            id: "more-options-close-icon"
                                        })
                                    ]
                                })
                            })
                        ]
                    }),
                    showOptions && /*#__PURE__*/ _jsx(SearchFormOptions, {
                        onChange: handleOptionsChange,
                        setShowOptions: setShowOptions,
                        checked: checkedOptions
                    })
                ]
            })
        ]
    });
};
var SearchFormWithContext = function(_param) /*#__PURE__*/ {
    var pageData = _param.pageData, formProps = _object_without_properties(_param, [
        "pageData"
    ]);
    return _jsx(SearchProvider, {
        pageData: pageData,
        children: /*#__PURE__*/ _jsx(SearchForm, _object_spread({}, formProps))
    });
};
export { SearchFormWithContext as SearchForm };
